// src/pages/programs/ProgramDetail.jsx
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  collection, query, where, onSnapshot, doc, updateDoc, serverTimestamp,
  getCountFromServer,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import useProgram from '@/hooks/useProgram';
import { formatCents } from '@/utils/programPricing';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  LinkIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ClockIcon,
  PencilSquareIcon,
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  ArrowTopRightOnSquareIcon,
  ChevronLeftIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  RectangleGroupIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

// ── helpers ────────────────────────────────────────────────────────────────────
function toDate(v) {
  if (!v) return null;
  if (v?.toDate) return v.toDate();
  if (v?.seconds) return new Date(v.seconds * 1000);
  return new Date(v);
}

const STATUS_CONFIG = {
  draft:    { label: 'Draft',    cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  open:     { label: 'Open',     cls: 'bg-[#b5f03d]/15 text-[#3d6010] border-[#b5f03d]/40' },
  closed:   { label: 'Closed',   cls: 'bg-zinc-100 text-zinc-600 border-zinc-200' },
  archived: { label: 'Archived', cls: 'bg-zinc-100 text-zinc-500 border-zinc-200' },
};

// On the dark header, status pill needs a slightly different treatment for contrast
const STATUS_CONFIG_DARK = {
  draft:    { label: 'Draft',    cls: 'bg-amber-400/20 text-amber-200 border-amber-400/40' },
  open:     { label: 'Open',     cls: 'bg-[#b5f03d]/20 text-[#b5f03d] border-[#b5f03d]/40' },
  closed:   { label: 'Closed',   cls: 'bg-white/10 text-white/70 border-white/20' },
  archived: { label: 'Archived', cls: 'bg-white/10 text-white/60 border-white/20' },
};

const NEXT_STATUS = {
  draft: { action: 'Open Registration', next: 'open' },
  open:  { action: 'Close Registration', next: 'closed' },
  closed: { action: 'Archive', next: 'archived' },
  archived: null,
};

// ── sub-components ─────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, color = 'lime', to }) {
  const colorMap = {
    lime:    'bg-[#b5f03d]/15 text-[#5a8a1f] ring-[#b5f03d]/40',
    emerald: 'bg-emerald-50 text-emerald-600 ring-emerald-200',
    amber:   'bg-amber-50 text-amber-600 ring-amber-200',
    zinc:    'bg-zinc-100 text-zinc-500 ring-zinc-200',
  };
  const content = (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] flex items-center gap-4 hover:border-[#b5f03d]/40 transition-all">
      <div className={`rounded-xl p-3 ring-1 ${colorMap[color]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>{value}</p>
        <p className="text-xs text-zinc-500 mt-0.5">{label}</p>
      </div>
    </div>
  );
  if (to) return <Link to={to}>{content}</Link>;
  return content;
}

// ── main ───────────────────────────────────────────────────────────────────────
export default function ProgramDetail() {
  const { clubId, programId } = useParams();
  const { program, loading } = useProgram(programId);

  const [stats, setStats] = useState({ total: 0, confirmed: 0, pending: 0, waitlist: 0, revenue: 0 });
  const [divisionCounts, setDivisionCounts] = useState({}); // { divisionId: number }
  const [changingStatus, setChangingStatus] = useState(false);
  const [copied, setCopied] = useState(false);

  // ── registration stats ───────────────────────────────────────────────────────
  // Must include clubId — the registrations rule under Phase 2 lockdown
  // gates club-staff reads on isClubStaff(resource.data.clubId), and a
  // programId-only query isn't statically pattern-matchable to that rule
  // so the listener fails permission-denied.
  useEffect(() => {
    if (!programId || !clubId) return;
    const q = query(
      collection(db, 'registrations'),
      where('clubId',    '==', clubId),
      where('programId', '==', programId),
    );
    const unsub = onSnapshot(q, (snap) => {
      let total = 0, confirmed = 0, pending = 0, waitlist = 0, revenue = 0;
      const divCounts = {};
      snap.docs.forEach((d) => {
        const data = d.data();
        if (data.status === 'archived' || data.status === 'cancelled') return;
        if (data.status === 'waitlist') { waitlist++; return; }
        total++;
        if (data.divisionId) {
          divCounts[data.divisionId] = (divCounts[data.divisionId] ?? 0) + 1;
        }
        const amt = data.pricing?.finalAmount ?? data.amountDue ?? 0;
        if (data.status === 'confirmed' || data.status === 'paid') {
          confirmed++;
          revenue += amt;
        } else {
          pending++;
        }
      });
      setStats({ total, confirmed, pending, waitlist, revenue });
      setDivisionCounts(divCounts);
    }, (err) => {
      if (import.meta.env.DEV) console.warn('[ProgramDetail] reg stats subscribe failed', err);
    });
    return unsub;
  }, [programId, clubId]);

  // ── copy link ────────────────────────────────────────────────────────────────
  const regUrl = `${window.location.origin}/p/${programId}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(regUrl);
      setCopied(true);
      toast.success('Registration link copied!');
      setTimeout(() => setCopied(false), 3000);
    } catch {
      toast.error('Could not copy link');
    }
  };

  // ── status change ────────────────────────────────────────────────────────────
  const handleStatusChange = async () => {
    if (!program) return;
    const next = NEXT_STATUS[program.status];
    if (!next) return;
    try {
      setChangingStatus(true);
      await updateDoc(doc(db, 'programs', programId), {
        status: next.next,
        updatedAt: serverTimestamp(),
      });
      toast.success(`Program is now ${next.next}`);
    } catch (err) {
      console.error('Status change error:', err);
      toast.error('Failed to update status');
    } finally {
      setChangingStatus(false);
    }
  };

  // ── render ───────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-[#f7faf5]">
        <div className="flex items-center justify-center py-24 text-zinc-400 text-sm">
          Loading program…
        </div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen bg-[#f7faf5]">
        <div className="text-center py-24">
          <p className="text-zinc-500">Program not found.</p>
          <Link to={`/clubs/${clubId}/programs`} className="text-[#5a8a1f] hover:text-[#3d6010] underline mt-2 inline-block text-sm">
            Back to Programs
          </Link>
        </div>
      </div>
    );
  }

  const badge = STATUS_CONFIG_DARK[program.status] ?? STATUS_CONFIG_DARK.draft;
  const nextAction = NEXT_STATUS[program.status];
  const regClose = toDate(program.registrationClose);
  const regOpen = toDate(program.registrationOpen);
  const earlyBirdDeadline = toDate(program.earlyBirdDeadline);

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6 pb-16">
      {/* Back */}
      <Link
        to={`/clubs/${clubId}/programs`}
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-[#5a8a1f] transition-colors"
      >
        <ChevronLeftIcon className="h-4 w-4" />
        All Programs
      </Link>

      {/* Pitch-dark header card */}
      <div className="bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
        <div className="px-6 pt-6 pb-6 bg-[#0d1f14] relative overflow-hidden">
          {/* Subtle lime grid pattern */}
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                'linear-gradient(to right, #b5f03d 1px, transparent 1px), linear-gradient(to bottom, #b5f03d 1px, transparent 1px)',
              backgroundSize: '32px 32px',
            }}
          />
          <div className="relative flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              {/* Lime medallion */}
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                <RectangleGroupIcon className="h-5 w-5 text-[#b5f03d]" />
              </div>
              <div className="min-w-0 flex-1">
                <p
                  className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#b5f03d] mb-1"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  Program
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                  <h1
                    className="uppercase text-white leading-[1.05] truncate"
                    style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 24 }}
                  >
                    {program.name}
                  </h1>
                  <span
                    className={`text-[11px] font-bold uppercase px-2.5 py-0.5 rounded-full border ${badge.cls}`}
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                  >
                    {badge.label}
                  </span>
                </div>

                {program.sport && (
                  <p
                    className="text-[12px] font-semibold uppercase text-white/70 mt-1.5"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                  >
                    {program.sport}
                  </p>
                )}

                {program.description && (
                  <p className="text-sm text-white/80 mt-2 leading-relaxed">{program.description}</p>
                )}

                {/* Date range */}
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-white/60">
                  {regOpen && <span>Opens {format(regOpen, 'MMM d, yyyy')}</span>}
                  {regClose && <span>Closes {format(regClose, 'MMM d, yyyy')}</span>}
                  {earlyBirdDeadline && (
                    <span className="text-[#b5f03d]/90">
                      Early bird until {format(earlyBirdDeadline, 'MMM d, yyyy')}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Fee block */}
            <div className="text-right shrink-0">
              <p
                className="text-3xl font-bold text-white"
                style={{ fontFamily: HEAD_FONT }}
              >
                {formatCents(program.defaultFee)}
              </p>
              <p
                className="text-[11px] font-bold uppercase text-white/60"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                per player
              </p>
              {program.earlyBirdFee > 0 && (
                <p
                  className="text-sm text-[#b5f03d] mt-0.5 font-semibold"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  {formatCents(program.earlyBirdFee)} early bird
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick-action grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <button
          type="button"
          onClick={copyLink}
          className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] hover:border-[#b5f03d]/40 transition-all flex flex-col items-start gap-2 text-left"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
            {copied ? (
              <CheckCircleIcon className="h-5 w-5 text-emerald-600" />
            ) : (
              <LinkIcon className="h-5 w-5 text-[#5a8a1f]" />
            )}
          </div>
          <p
            className="text-sm font-bold uppercase text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.4px' }}
          >
            {copied ? 'Copied!' : 'Copy Link'}
          </p>
        </button>

        <a
          href={regUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] hover:border-[#b5f03d]/40 transition-all flex flex-col items-start gap-2"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
            <ArrowTopRightOnSquareIcon className="h-5 w-5 text-[#5a8a1f]" />
          </div>
          <p
            className="text-sm font-bold uppercase text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.4px' }}
          >
            Preview
          </p>
        </a>

        <Link
          to={`/clubs/${clubId}/programs/${programId}/edit`}
          className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] hover:border-[#b5f03d]/40 transition-all flex flex-col items-start gap-2"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
            <PencilIcon className="h-5 w-5 text-[#5a8a1f]" />
          </div>
          <p
            className="text-sm font-bold uppercase text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.4px' }}
          >
            Edit
          </p>
        </Link>

        <Link
          to={`/clubs/${clubId}/programs/create`}
          state={{ prefill: {
            name: `Copy of ${program.name}`,
            sport: program.sport,
            description: program.description,
            defaultFee: program.defaultFee,
            earlyBirdFee: program.earlyBirdFee,
            earlyBirdDeadline: program.earlyBirdDeadline,
            registrationOpen: program.registrationOpen,
            registrationClose: program.registrationClose,
            divisions: program.divisions,
            requiredDocTypes: program.requiredDocTypes,
            waivers: program.waivers,
            extraFields: program.extraFields,
          }}}
          className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] hover:border-[#b5f03d]/40 transition-all flex flex-col items-start gap-2"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
            <DocumentDuplicateIcon className="h-5 w-5 text-[#5a8a1f]" />
          </div>
          <p
            className="text-sm font-bold uppercase text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.4px' }}
          >
            Duplicate
          </p>
        </Link>

        <Link
          to={`/clubs/${clubId}/programs/${programId}/registrations`}
          className="rounded-2xl border border-[#0d1f14] bg-[#0d1f14] p-4 shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] hover:bg-[#0d1f14]/90 transition-all flex flex-col items-start gap-2"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#b5f03d]/20 ring-1 ring-[#b5f03d]/40">
            <ClipboardDocumentListIcon className="h-5 w-5 text-[#b5f03d]" />
          </div>
          <p
            className="text-sm font-bold uppercase text-white"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.4px' }}
          >
            Registrations
          </p>
        </Link>
      </div>

      {/* Status toggle row */}
      {nextAction && (
        <div className="flex justify-end">
          <button
            type="button"
            disabled={changingStatus}
            onClick={handleStatusChange}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 disabled:opacity-50 transition-all"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
          >
            {changingStatus ? 'Updating…' : nextAction.action}
          </button>
        </div>
      )}

      {/* Stats row */}
      <div className={`grid gap-3 ${stats.waitlist > 0 ? 'grid-cols-2 lg:grid-cols-5' : 'grid-cols-2 lg:grid-cols-4'}`}>
        <StatCard
          icon={UserGroupIcon}
          label="Total Registered"
          value={stats.total}
          color="lime"
          to={`/clubs/${clubId}/programs/${programId}/registrations`}
        />
        <StatCard
          icon={CheckCircleIcon}
          label="Spot held"
          value={stats.confirmed}
          color="emerald"
        />
        <StatCard
          icon={ClockIcon}
          label="Hasn't paid yet"
          value={stats.pending}
          color="amber"
        />
        {stats.waitlist > 0 && (
          <StatCard
            icon={ClockIcon}
            label="Waitlisted"
            value={stats.waitlist}
            color="zinc"
            to={`/clubs/${clubId}/programs/${programId}/registrations`}
          />
        )}
        <StatCard
          icon={CurrencyDollarIcon}
          label="Revenue Collected"
          value={formatCents(stats.revenue)}
          color="lime"
        />
      </div>

      {/* Divisions */}
      {program.divisions?.length > 0 && (
        <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]">
          <p
            className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-4"
            style={{ fontFamily: HEAD_FONT }}
          >
            Divisions
          </p>
          <div className="space-y-2">
            {program.divisions.map((div) => (
              <div
                key={div.id}
                className="rounded-xl bg-zinc-50 border border-zinc-100 px-4 py-3"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#0d1f14] text-sm">{div.name || 'Unnamed Division'}</p>
                    {div.teamName && (
                      <p className="text-xs text-zinc-400 mt-0.5">→ {div.teamName}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>
                      {div.fee != null ? formatCents(div.fee) : formatCents(program.defaultFee)}
                    </p>
                    {div.maxPlayers ? (
                      <p className="text-xs text-zinc-400">
                        {divisionCounts[div.id] ?? 0}/{div.maxPlayers} spots
                      </p>
                    ) : divisionCounts[div.id] != null ? (
                      <p className="text-xs text-zinc-400">{divisionCounts[div.id]} registered</p>
                    ) : null}
                  </div>
                </div>
                {/* Capacity bar */}
                {div.maxPlayers > 0 && (() => {
                  const filled = divisionCounts[div.id] ?? 0;
                  const pct = Math.min(100, Math.round((filled / div.maxPlayers) * 100));
                  const isFull = filled >= div.maxPlayers;
                  return (
                    <div className="mt-2">
                      <div className="h-1.5 w-full rounded-full bg-zinc-200 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${isFull ? 'bg-red-400' : pct > 75 ? 'bg-amber-400' : 'bg-[#b5f03d]'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      {isFull && (
                        <p className="text-[10px] text-red-500 font-semibold mt-0.5">Full</p>
                      )}
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Required docs */}
      {program.requiredDocTypes?.length > 0 && (
        <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]">
          <p
            className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-3"
            style={{ fontFamily: HEAD_FONT }}
          >
            Required Documents
          </p>
          <div className="flex flex-wrap gap-2">
            {program.requiredDocTypes.map((d) => (
              <span
                key={d}
                className="text-xs font-bold uppercase px-3 py-1 rounded-full bg-[#b5f03d]/15 text-[#3d6010] border border-[#b5f03d]/40"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                {d}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Waivers */}
      {program.waivers?.length > 0 && (
        <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] space-y-4">
          <p
            className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
            style={{ fontFamily: HEAD_FONT }}
          >
            Waivers ({program.waivers.length})
          </p>
          {program.waivers.map((w, i) => (
            <div key={w.id ?? i} className="rounded-xl bg-zinc-50 border border-zinc-100 p-4">
              <p className="text-sm font-semibold text-[#0d1f14] mb-1">{w.name || `Waiver ${i + 1}`}</p>
              <p className="text-xs text-zinc-500 line-clamp-3 leading-relaxed">{w.text}</p>
            </div>
          ))}
        </div>
      )}

      {/* Registration link card */}
      <div className="rounded-2xl border border-dashed border-[#b5f03d]/60 bg-[#b5f03d]/10 p-6">
        <p
          className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-2"
          style={{ fontFamily: HEAD_FONT }}
        >
          Registration Link
        </p>
        <div className="flex items-center gap-3">
          <code className="flex-1 text-xs bg-white border border-[#b5f03d]/40 rounded-lg px-3 py-2 text-[#0d1f14] truncate font-mono">
            {regUrl}
          </code>
          <button
            type="button"
            onClick={copyLink}
            className="shrink-0 inline-flex items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97]"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <p className="text-xs text-[#5a8a1f] mt-2">
          Send this link to players or families you want to invite to register.
        </p>
      </div>
      </div>
    </div>
  );
}
